import React, { useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'

import { useFetchList } from '../../store'

import {
	ClinicalEpisode,
	ProcessChangesHistory,
	Requisition,
	Appointment,
} from 'models'

import NoData from 'components/general/NoData/NoData'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import { useRecoilState, useRecoilValue } from 'recoil'
import {
	controllerFilters,
	currentDetailLevelState,
	openedRequisitionState,
	searchProcessString,
} from 'store/atoms'
import useTermSearch from 'hooks/useTermSearch'
import { useDeepCompareEffect, useLocation } from 'react-use'
import RequisitionCardV2 from 'components/cards/RequisitionCard/RequisitionCardV2'
import EpisodeSeparatorCard from 'components/cards/EpisodesSeparatorCard/EpisodesSeparatorCard'
import { useDrawerContext } from 'context/Drawer/DrawerContext'
import { getScrollParentOffsetY } from '@jimmyboys/liita-ui/dist/utils/getScrollParent'

const ClinicalRequisitionsController = () => {
	const { i18n, screenSize } = useGlobal()
	const { clinicalProcessId } = useParams()
	const location = useLocation()
	const { dispatch } = useDrawerContext()
	const collectionRef = useRef(null)
	const requisitionsUri = `/clinical_processes/${clinicalProcessId}/requisitions`
	const processChangesHistoryUri = `/clinical_processes/${clinicalProcessId}/history`
	const clinicalEpisodesUri = `/clinical_processes/${clinicalProcessId}/clinical_episodes`
	const appointmentsUri = `clinical_processes/${clinicalProcessId}/appointments`
	const [requisitionFilters, setRequisitionFilters] =
		useRecoilState(controllerFilters)
	const processString = useRecoilValue(searchProcessString)

	const anchorTag = location.hash.trim()?.substring(1)
	const [, scrollTo] = getScrollParentOffsetY(collectionRef.current)

	const [selectedRequisition, setSelectedRequisition] = useRecoilState(
		openedRequisitionState
	)

	const detailLevel = useRecoilValue(currentDetailLevelState)

	const cardLabels = {
		goToRequisition: i18n`Go to requisition`,
		requisition: i18n`Requisition`,
		execution: i18n`Execution`,
		applicant: i18n`Applicant`,
		type: i18n`Type`,
		dateAndPlace: i18n`Date/Place`,
		outsideNetwork: i18n`outside network`,
		medicalActs: i18n`Medical Acts`,
		clinicalInformation: i18n`Clinical Information`,
		exams: i18n`Exams`,
		documents: i18n`Documents`,
		notDefined: i18n`Not defined`,
		changesHistory: i18n`Changes History`,
		requestedBy: i18n`Requested by`,
		toTheDateOf: i18n`to the date of`,
		comments: i18n`Comments`,
	}

	const { state, contents } = useFetchList(
		[
			requisitionsUri,
			clinicalEpisodesUri,
			processChangesHistoryUri,
			appointmentsUri,
		],
		[
			Requisition.fromJSON,
			ClinicalEpisode.fromJSON,
			ProcessChangesHistory.fromJSON,
			Appointment.fromJSON,
		]
	)

	const isLoading = state === 'loading'

	const [
		requisitionsData,
		clinicalEpisodesData = [],
		processChangesHistory = [],
		appointments = [],
	] = contents || []

	const cardLayout = 'vertical'
	let requisitions = requisitionsData || []

	processChangesHistory?.sort(
		(a, b) =>
			new Date(b.modificationDate.timestamp) -
			new Date(a.modificationDate.timestamp)
	)

	let groupedHistory = {}
	groupedHistory = (processChangesHistory || []).reduce((acc, elem) => {
		if (elem.requisitionId) {
			if (acc[elem.requisitionId] === undefined) {
				acc[elem.requisitionId] = []
			}
			acc[elem.requisitionId].push(elem)
		}

		return acc
	}, {})

	requisitions = requisitions.map((json) => {
		return {
			...json,
			sortParam:
				json.executionDate?.timestamp ||
				json.scheduledDate?.timestamp ||
				json.requisitionDate?.timestamp,
			$cardName: 'RequisitionCardV2',
			$cardLabels: cardLabels,
			$layout: cardLayout,
			$displayAnchorLink: true,
			$detailLevel: detailLevel,
			$highlight:
				anchorTag === json.anchorId || json.id === selectedRequisition,
			$action: () => {
				setSelectedRequisition(json.id)
				dispatch({
					action: 'OPEN_DRAWER',
					payload: {
						type: 'RequisitionDrawer',
						data: {
							...json,
							history: groupedHistory[json.id],
							appointments,
						},
					},
				})
			},
		}
	})

	const clinicalEpisodes =
		requisitions.length && clinicalEpisodesData
			? clinicalEpisodesData.slice(1).map((json, index) => {
					return {
						...json,
						prevEpisode: clinicalEpisodesData[index],
						sortParam: json.date.timestamp - 1001,
						$cardName: 'EpisodesSeparatorCard',
						$layout: cardLayout,
					}
			  })
			: []

	const data = [...requisitions, ...clinicalEpisodes].sort(
		(a, b) => b.sortParam - a.sortParam
	)

	const { searchMatches, setQuery } = useTermSearch(data, ['id'])

	useEffect(() => {
		setQuery(processString.replace(/[^0-9]/g, ''))
	}, [processString, setQuery])

	const { filters } = requisitionFilters['requisitions']

	const finalCollection = useMemo(() => {
		if (searchMatches.length) {
			let value = searchMatches
			if (filters?.type?.length) {
				value = value.filter((elem) => {
					return filters?.type?.includes(elem?.type?.name)
				})
			}

			return value
		}
	}, [searchMatches, filters])

	const requisitionTypes = useMemo(() => {
		return data
			? data.length
				? [
						{ value: '', text: i18n`Any` },
						...[...new Set(data.map((ele) => ele.type?.name))]
							.filter(Boolean)
							.map((ele) => ({
								value: ele,
								text: i18n.translate(ele),
							})),
				  ]
				: [{ value: '', text: i18n`Any` }]
			: [{ value: '', text: i18n`Any` }]
	}, [data, i18n])

	const requisitionTypeValuesList = useMemo(() => {
		const requisitionTypeValuesList = filters?.type?.length
			? requisitionTypes.map((elem) => {
					return filters?.type?.includes(elem.value)
						? { ...elem, $isSelected: true }
						: elem
			  })
			: [
					{ ...requisitionTypes[0], $isSelected: true },
					...requisitionTypes.slice(1),
			  ]

		return requisitionTypeValuesList
	}, [filters, requisitionTypes])

	useDeepCompareEffect(() => {
		setRequisitionFilters((prev) => {
			return {
				...prev,
				requisitions: {
					...prev['requisitions'],
					filterValuesList: [requisitionTypeValuesList],
				},
			}
		})
	}, [requisitionTypeValuesList, setRequisitionFilters, requisitionFilters])

	useEffect(() => {
		if (anchorTag && finalCollection?.length) {
			const selectedRequisition = document.getElementById(anchorTag)
			const elementPos = selectedRequisition?.getBoundingClientRect()

			if (elementPos) {
				let itemPos = elementPos.top

				scrollTo &&
					scrollTo(
						screenSize === 'small' ? itemPos : itemPos - 100,
						0,
						'smooth'
					)
			}
		}
	}, [screenSize, finalCollection, anchorTag, scrollTo])

	return (
		<div ref={collectionRef}>
			{!searchMatches.length ? (
				<NoData
					applyMaxWidth
					paddings={['normal', 'normal']}
					message={
						isLoading
							? i18n`Loading`
							: i18n`This process has no extra data to show.`
					}
				/>
			) : (
				(finalCollection ?? []).map((item, idx) =>
					item.$cardName === 'EpisodesSeparatorCard' ? (
						<EpisodeSeparatorCard key={idx} data={item} />
					) : (
						<RequisitionCardV2
							id={`R_${item?.id}`}
							key={idx}
							data={item}
						/>
					)
				)
			)}
		</div>
	)
}

export default ClinicalRequisitionsController
